import React, { useCallback, useEffect, useRef } from "react";
import {
  AutoComplete,
  Flex,
  Input,
  Popconfirm,
  Space,
  Spin,
  Typography,
} from "antd";

import FlatButton from "@/components/button/button";
import TableComponent from "@/components/table/table";
import { UserSchema } from "@/schemas";
import {
  DeleteOutlined,
  EditOutlined,
  UserAddOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { protectedRequest } from "@/services";
import { useDebouncedCallback } from "@/hooks/use-debounced-callback";
import _ from "lodash";

const { Text } = Typography;
const { Column } = TableComponent;

const onRequestGetAllUser = async (callback: () => void) => {
  try {
    const response = await protectedRequest<any, UserSchema[]>(
      {
        url: "/user/gets",
        method: "GET",
      },
      callback
    );

    return response.data;
  } catch (error) {
    callback();
  }
};

const onRequestDeleteUser = async (id: number, callback: () => void) => {
  try {
    await protectedRequest<any, UserSchema[]>(
      {
        url: `/user/delete/${id}`,
        method: "DELETE",
      },
      callback
    );
  } catch (error) {
    callback();
  }
};

const UserManagement: React.FC = () => {
  const userTemps = useRef<UserSchema[]>([]);

  const [users, setUsers] = React.useState<UserSchema[]>([]);

  const [loading, setLoading] = React.useState(false);

  const navigate = useNavigate();

  const userRequestUsers = useCallback(async () => {
    setLoading(true);
    try {
      const result = await onRequestGetAllUser(() => {
        navigate("/admin/login", { replace: true });
      });
      setUsers(result ?? []);
      userTemps.current = result ?? [];
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    userRequestUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDeleteUser = useCallback(
    async (idUser: number) => {
      try {
        await onRequestDeleteUser(idUser, () => {
          navigate("/admin/login", { replace: true });
        });
        const newUsers = users.filter((user) => user.id !== idUser);
        setUsers(newUsers);
      } catch (error) {
        console.log(error);
      }
    },
    [navigate, users]
  );

  const onUpdateUser = useCallback(
    async (user: UserSchema) => {
      navigate("/admin/create-user", { state: { user: user, mode: "update" } });
    },
    [navigate]
  );

  const onRequestSearch = useDebouncedCallback((value: any) => {
    if (value && value.target.value === "") {
      setUsers(userTemps.current);
    } else {
      const data = _.filter(userTemps.current, function (s) {
        const checkName =
          s != null &&
          s.name.toLowerCase().includes(value.target.value.toLowerCase());
        const checkEmail =
          s != null &&
          s.email.toLowerCase().includes(value.target.value.toLowerCase());

        return checkName || checkEmail;
      });
      setUsers(data);
    }
  }, 500);

  return (
    <Flex gap="middle" align="center" vertical>
      <Flex
        style={{
          width: "100%",
          height: 80,
          padding: 16,
        }}
        justify={"space-between"}
        align={"center"}
      >
        <Text style={{ fontSize: 24, color: "#40C1A2" }}>ユーザー管理</Text>

        <Flex>
          <Space size={16}>
            <AutoComplete
              popupMatchSelectWidth={252}
              style={{ width: 300 }}
              options={[]}
              size="large"
            >
              <Input.Search
                size="large"
                placeholder="ユーザーを検索する"
                onChange={onRequestSearch}
              />
            </AutoComplete>
            <FlatButton
              type="primary"
              size="large"
              icon={<UserAddOutlined />}
              onClick={() => navigate("/admin/create-user")}
            >
              ユーザー作成
            </FlatButton>
          </Space>
        </Flex>
      </Flex>

      <Spin
        spinning={loading}
        size="large"
        className="app-loading-wrapper"
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.44)",
        }}
      />

      {users.length > 0 && (
        <TableComponent<UserSchema>
          dataSource={users}
          rowKey={(record) => record.key}
          height="100%"
        >
          <Column title="名前" dataIndex="name" key="name" />
          <Column title="メールアドレス" dataIndex="email" key="email" />
          {/* <Column<UserSchema>
          title="Role"
          dataIndex="role"
          key="role"
          render={(tag: string) => (
            <Tag color="blue" key={tag}>
              {tag}
            </Tag>
          )}
        /> */}
          <Column
            title="アクション"
            key="action"
            render={(record: any) => (
              <Space size="middle">
                <FlatButton
                  onClick={() => onUpdateUser(record)}
                  type="default"
                  icon={<EditOutlined />}
                >
                  更新
                </FlatButton>

                <Popconfirm
                  title="ユーザーを削除する"
                  description="このユーザーを削除してもよろしいですか?"
                  onConfirm={() => onDeleteUser(record.id)}
                  icon={<QuestionCircleOutlined style={{ color: "red" }} />}
                >
                  <FlatButton type="primary" danger icon={<DeleteOutlined />}>
                    削除する
                  </FlatButton>
                </Popconfirm>
              </Space>
            )}
          />
        </TableComponent>
      )}
    </Flex>
  );
};

export default UserManagement;
